import { ReactNode, useMemo } from 'react';

import { Icons, notify } from '@cast/design-system';

import { PreparedWorkload } from 'features/workload-optimization';
import { useUpdateWorkloadMutationV2 } from 'hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';

import { NotificationMessage } from '../../_components/NotificationMessage';
import {
  canEditWorkload,
  canEnablePredictiveWorkload,
  getWorkloadEnablePredictiveScalingPatchBody,
  isPredictiveWorkloadEnabled,
} from '../../_utils';
import { useConfirmationDialog } from '../useConfirmationDialog';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type UseTogglePredictiveWorkloadsReturn = {
  workloads: PreparedWorkload[];
};

export const useTogglePredictiveWorkloads = ({
  workloads,
}: UseTogglePredictiveWorkloadsReturn) => {
  const { cluster } = useCluster();

  const { mutate } = useUpdateWorkloadMutationV2();

  const { openConfirmationDialog, closeConfirmationDialog } =
    useConfirmationDialog();

  // Filter out workloads that cannot be edited
  const { canEnableEditableWorkloads, canDisableEditableWorkloads } =
    useMemo(() => {
      const editableWorkloads = workloads.filter(canEditWorkload);

      return {
        canEnableEditableWorkloads: editableWorkloads.filter(
          canEnablePredictiveWorkload
        ),

        canDisableEditableWorkloads: editableWorkloads.filter(
          isPredictiveWorkloadEnabled
        ),
      };
    }, [workloads]);

  const enablePredictiveWorkloads = ({
    successMsg,
    errorMsg,
  }: {
    successMsg: ReactNode;
    errorMsg: ReactNode;
  }) => {
    if (canEnableEditableWorkloads.length === 0) {
      return;
    }
    const runMutation = () =>
      mutate(
        [
          ...canEnableEditableWorkloads.map(({ id }) =>
            getWorkloadEnablePredictiveScalingPatchBody({
              clusterId: cluster.id,
              workloadId: id,
              enabled: true,
            })
          ),
        ],
        {
          onSuccess: () => {
            notify.success(
              <NotificationMessage variant="success">
                {successMsg}
              </NotificationMessage>
            );
          },
          onError: () => {
            notify.error(
              <NotificationMessage variant="error">
                {errorMsg}
              </NotificationMessage>
            );
          },
          onSettled: closeConfirmationDialog,
        }
      );

    // found at least one workload that is not managed
    if (canEnableEditableWorkloads.some((w) => !w.isVerticalScalingEnabled)) {
      return openConfirmationDialog({
        highlightBody: true,
        highlightBodyVariant: 'predictive',
        title: 'Enable automatic optimization?',
        body:
          canEnableEditableWorkloads.length === 1
            ? 'Enabling predictive scaling will turn on automatic optimization for this workload.'
            : `Enabling predictive scaling will turn on automatic optimization for any workloads not currently automated.`,
        variant: 'check',
        testId: 'enable-predictive-workloads-confirmation-dialog',
        confirmButtonProps: {
          onClick: () => runMutation(),
        },
        cancelButtonProps: {
          onClick: closeConfirmationDialog,
        },
        confirmBtnLabel: 'Enable',
        cancelBtnLabel: 'Cancel',
        icon: <Icons.ToggleRight />,
      });
    }
    return runMutation();
  };

  const disablePredictiveWorkloads = ({
    successMsg,
    errorMsg,
  }: {
    successMsg: ReactNode;
    errorMsg: ReactNode;
  }) =>
    canDisableEditableWorkloads.length > 0 &&
    mutate(
      [
        ...canDisableEditableWorkloads.map(({ id }) =>
          getWorkloadEnablePredictiveScalingPatchBody({
            clusterId: cluster.id,
            workloadId: id,
            enabled: false,
          })
        ),
      ],
      {
        // todo(mike) grey instead of green
        onSuccess: () => {
          notify.success(
            <NotificationMessage variant="success">
              {successMsg}
            </NotificationMessage>
          );
        },
        onError: () => {
          notify.error(
            <NotificationMessage variant="error">
              {errorMsg}
            </NotificationMessage>
          );
        },
      }
    );

  return {
    enablePredictiveWorkloads,
    disablePredictiveWorkloads,

    canEnableWorkloadsCount: canEnableEditableWorkloads.length,
    canDisableEditableWorkloads: canDisableEditableWorkloads.length,
  };
};
